import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiFilter, FiX } from 'react-icons/fi';
import ApartmentCard from '../components/ApartmentCard';
import SearchBox from '../components/SearchBox';
import api from '../services/api';

const Apartments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [apartments, setApartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    type: searchParams.get('type') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    guests: searchParams.get('guests') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    sort: searchParams.get('sort') || 'featured'
  });

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await api.getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchApartments = async () => {
      setLoading(true);
      try {
        const params = { ...filters, page: pagination.page };
        Object.keys(params).forEach(key => {
          if (!params[key]) delete params[key];
        });

        const data = await api.getApartments(params);
        setApartments(data.apartments);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Error fetching apartments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchApartments();
  }, [filters, pagination.page]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));

    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      type: '',
      minPrice: '',
      maxPrice: '',
      guests: '',
      bedrooms: '',
      checkIn: '',
      checkOut: '',
      sort: 'featured'
    });
    setSearchParams({});
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== 'featured');

  const styles = {
    header: {
      background: '#f7f7f7',
      padding: '40px 24px'
    },
    container: {
      maxWidth: '1280px',
      margin: '0 auto'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '24px'
    },
    content: {
      padding: '40px 24px'
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    results: {
      color: '#555'
    },
    actions: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    filterBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'white',
      cursor: 'pointer',
      fontWeight: '500'
    },
    sortSelect: {
      padding: '10px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'white',
      cursor: 'pointer',
      fontWeight: '500'
    },
    filterPanel: {
      background: 'white',
      border: '1px solid #eee',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      display: showFilters ? 'block' : 'none'
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '16px'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    filterLabel: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#222'
    },
    filterInput: {
      padding: '10px 12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    clearBtn: {
      padding: '10px 16px',
      border: 'none',
      borderRadius: '8px',
      background: '#f7f7f7',
      cursor: 'pointer',
      fontWeight: '500',
      marginTop: '16px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '24px'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '40px'
    },
    pageBtn: {
      padding: '10px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'white',
      cursor: 'pointer'
    },
    pageBtnActive: {
      background: '#222',
      color: 'white',
      border: '1px solid #222'
    },
    empty: {
      textAlign: 'center',
      padding: '60px 24px'
    }
  };

  return (
    <>
      <header style={styles.header}>
        <div style={styles.container}>
          <h1 style={styles.title}>Find Your Perfect Apartment</h1>
          <SearchBox variant="inline" />
        </div>
      </header>

      <section style={styles.content}>
        <div style={styles.container}>
          <div style={styles.toolbar}>
            <p style={styles.results}>
              {pagination.total} apartment{pagination.total !== 1 ? 's' : ''} found
            </p>

            <div style={styles.actions}>
              <button
                style={{
                  ...styles.filterBtn,
                  ...(hasActiveFilters ? { borderColor: '#1a3a5c', color: '#1a3a5c' } : {})
                }}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FiFilter />
                Filters
                {hasActiveFilters && <span style={{ background: '#1a3a5c', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</span>}
              </button>

              <select
                style={styles.sortSelect}
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Top Rated</option>
                <option value="newest">Newest</option>
              </select>
            </div>
          </div>

          {/* Filter Panel */}
          <div style={styles.filterPanel}>
            <div style={styles.filterGrid}>
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Location</label>
                <select
                  style={styles.filterInput}
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                >
                  <option value="">All locations</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc.slug}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Type</label>
                <select
                  style={styles.filterInput}
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <option value="">All types</option>
                  <option value="Studio">Studio</option>
                  <option value="1-Bed">1 Bedroom</option>
                  <option value="2-Bed">2 Bedrooms</option>
                  <option value="3-Bed">3 Bedrooms</option>
                  <option value="Penthouse">Penthouse</option>
                </select>
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Min Price (£/night)</label>
                <input
                  type="number"
                  style={styles.filterInput}
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                />
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Max Price (£/night)</label>
                <input
                  type="number"
                  style={styles.filterInput}
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                />
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Guests</label>
                <select
                  style={styles.filterInput}
                  value={filters.guests}
                  onChange={(e) => handleFilterChange('guests', e.target.value)}
                >
                  <option value="">Any</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                    <option key={n} value={n}>{n}+ guests</option>
                  ))}
                </select>
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Bedrooms</label>
                <select
                  style={styles.filterInput}
                  value={filters.bedrooms}
                  onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="0">Studio</option>
                  {[1, 2, 3, 4].map(n => (
                    <option key={n} value={n}>{n}+ bedrooms</option>
                  ))}
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <button style={styles.clearBtn} onClick={clearFilters}>
                <FiX style={{ marginRight: '8px' }} />
                Clear all filters
              </button>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : apartments.length > 0 ? (
            <>
              <div style={styles.grid}>
                {apartments.map(apartment => (
                  <ApartmentCard key={apartment._id} apartment={apartment} />
                ))}
              </div>

              {pagination.pages > 1 && (
                <div style={styles.pagination}>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      style={{
                        ...styles.pageBtn,
                        ...(page === pagination.page ? styles.pageBtnActive : {})
                      }}
                      onClick={() => setPagination(prev => ({ ...prev, page }))}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={styles.empty}>
              <h3>No apartments found</h3>
              <p style={{ color: '#555', marginTop: '8px' }}>
                Try adjusting your filters or search criteria
              </p>
              {hasActiveFilters && (
                <button
                  style={{ ...styles.filterBtn, marginTop: '16px' }}
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Apartments;
